import type { AdminDomainDto, AdminMemberDto, AdminMembersDto, MeResponse, MemberDto, PageDto, RelinkRequestDto, RoleKind, SystemAccountDto, TenantRole } from '@dbrain/shared';
import { Alert, Badge, Button, ConfirmDialog, Dialog, Icons, Menu, MenuItem, MenuSeparator, Select, Skeleton } from '@dbrain/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useCompanyZone, useMe, useMembers } from '../../api/hooks.js';
import { TeamsCard } from '../../components/admin/IntegrationCards.js';
import { CsvLink, DataTable, FilterChips, FilterMenu, Pagination, SearchBox, sortState, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { useCapabilities } from '../../experience.js';
import { formatCount, lastSignIn } from '@dbrain/app-core/format';
import { HandOver, InviteCard, NewOrganizationCard } from '../TeamPage.js';
import { AdminLayout, isOrganizationAdmin, SubTabs } from './AdminLayout.js';

/**
 * Yönetim → Erişim ve yetkiler (YonetimErisim.dc.html, 06 §23.4): the
 * members, the directory's domains and the system accounts, each listed by
 * the server. In the kurumsal edition roles and domains come from the
 * directory: here access is only turned off and on; nobody is removed.
 */
type Tab = 'members' | 'domains' | 'system' | 'teams';

const descByDefault = (key: string) => key === 'lastSignIn';

export const adminKeys = {
  members: (query: string) => ['admin', 'members', query] as const,
  domains: ['admin', 'domains'] as const,
  system: ['admin', 'system-accounts'] as const,
  relinks: ['admin', 'relinks'] as const,
};

/** The organization's domains (a domain admin: theirs), for the filters and the domain names. */
export function useAdminDomains(enabled = true) {
  return useQuery({
    queryKey: adminKeys.domains,
    queryFn: () => api<PageDto<AdminDomainDto>>('GET', '/api/admin/domains?size=50&sort=name'),
    enabled,
    staleTime: 60_000,
  });
}

export function AccessPage() {
  const { t } = useTranslation();
  const { tab = '' } = useParams();
  const me = useMe().data;
  const capabilities = useCapabilities();
  if (!me) return null;
  const organization = isOrganizationAdmin(me);
  const tabs: Tab[] = ['members', ...(capabilities.directoryTeams ? (['domains'] as const) : (['teams'] as const)), ...(capabilities.breakGlass && organization ? (['system'] as const) : [])];
  if (!tabs.includes(tab as Tab)) return <Navigate to="/admin/access/members" replace />;
  return (
    <AdminLayout section="access" title={t('admin.sections.access')}>
      <AccessContent me={me} tab={tab as Tab} tabs={tabs} />
    </AdminLayout>
  );
}

function AccessContent({ me, tab, tabs }: { me: MeResponse; tab: Tab; tabs: Tab[] }) {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const organization = isOrganizationAdmin(me);
  const members = useQuery({ queryKey: adminKeys.members('count'), queryFn: () => api<AdminMembersDto>('GET', '/api/admin/members?size=10&status=active'), staleTime: 30_000 });
  const domains = useAdminDomains(capabilities.directoryTeams);
  const system = useQuery({ queryKey: adminKeys.system, queryFn: () => api<SystemAccountDto[]>('GET', '/api/admin/system-accounts'), enabled: tabs.includes('system') });
  const counts: Record<Tab, number | undefined> = {
    members: members.data?.counts.all,
    domains: domains.data?.total,
    system: system.data?.length,
    teams: undefined,
  };
  return (
    <div className="stack admin-access">
      {capabilities.directoryLogin ? (
        <div className="admin-info" role="note" data-testid="access-directory-info">
          <Icons.info size={16} />
          <span>{t('admin.access.directoryInfo')}</span>
          {organization ? (
            <Link to="/admin/identity" className="admin-info__link">
              {t('admin.sections.identity')} →
            </Link>
          ) : null}
        </div>
      ) : null}
      {capabilities.directoryLogin && organization ? <RelinkCard /> : null}
      {me.tenant.kind === 'personal' && capabilities.publicSignup ? <NewOrganizationCard /> : null}
      <SubTabs
        label={t('admin.access.tabsLabel')}
        items={tabs.map((id) => ({ to: `/admin/access/${id}`, label: t(`admin.access.tabs.${id}`), count: counts[id], testId: `access-tab-${id}` }))}
      />
      {tab === 'members' ? <MembersTab me={me} /> : null}
      {tab === 'domains' ? <DomainsTab /> : null}
      {tab === 'system' ? <SystemTab accounts={system.data} loading={system.isPending} error={system.error} /> : null}
      {tab === 'teams' ? <TeamsCard admin={organization} /> : null}
    </div>
  );
}

/** "Rol şirket dizininden gelir": roles as the directory gives them, with the lock. */
function RoleCell({ member, me, onRole }: { member: AdminMemberDto; me: MeResponse; onRole?: (role: TenantRole) => void }) {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const tone = member.roleKind === 'owner' ? 'neutral' : member.roleKind === 'domain_admin' ? 'info' : member.roleKind === 'admin' ? 'accent' : 'neutral';
  const canChange = !capabilities.directoryLogin && onRole && member.userId !== me.user.id && (me.tenant.role === 'owner' || member.role !== 'owner');
  return (
    <span className="role-cell">
      {canChange ? (
        <Select size="sm" aria-label={t('admin.access.columns.role')} value={member.role} onChange={(e) => onRole(e.target.value as TenantRole)} data-testid="member-role">
          {(me.tenant.role === 'owner' ? ['owner', 'admin', 'member'] : ['admin', 'member']).map((role) => (
            <option key={role} value={role}>
              {t(`roles.tenant.${role}`)}
            </option>
          ))}
        </Select>
      ) : (
        <Badge tone={tone} className={`role-badge role-badge--${member.roleKind}`}>
          {t(`admin.access.roles.${member.roleKind}`)}
        </Badge>
      )}
      {member.directory ? (
        <span className="role-cell__lock" title={t('admin.access.roleFromDirectory')} aria-label={t('admin.access.roleFromDirectory')} role="img">
          <Icons.lock size={14} />
        </span>
      ) : null}
    </span>
  );
}

export function LastSignIn({ at }: { at: string | null }) {
  const { t, i18n } = useTranslation();
  const value = lastSignIn(at, i18n.language, new Date(), useCompanyZone());
  const stale = value.kind === 'days' && value.stale;
  const text =
    value.kind === 'never'
      ? t('admin.access.lastSignIn.never')
      : value.kind === 'today'
        ? t('admin.access.lastSignIn.today', { time: value.time })
        : value.kind === 'yesterday'
          ? t('admin.access.lastSignIn.yesterday', { time: value.time })
          : value.kind === 'days'
            ? t('admin.access.lastSignIn.days', { count: value.days })
            : value.date;
  return (
    <span className={stale ? 'last-sign-in last-sign-in--stale' : value.kind === 'never' ? 'last-sign-in muted' : 'last-sign-in'} data-stale={stale || undefined}>
      {text}
    </span>
  );
}

function StatusBadge({ status }: { status: AdminMemberDto['status'] }) {
  const { t } = useTranslation();
  if (status === 'deactivated') {
    return (
      <Badge tone="danger">
        <span className="ui-dot ui-dot--danger" aria-hidden="true" />
        {t('admin.access.status.deactivated')}
      </Badge>
    );
  }
  return (
    <Badge tone="success">
      <span className="ui-dot ui-dot--success" aria-hidden="true" />
      {t('admin.access.status.active')}
    </Badge>
  );
}

function MembersTab({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const organization = isOrganizationAdmin(me);
  const list = useListParams<'domain' | 'role' | 'status'>({ status: 'active' });
  const { sort, onSort } = sortState(list, 'lastSignIn', descByDefault);
  const status = list.get('status');
  // "all" in the address: no status filter (the default is the active members)
  const query = new URLSearchParams([...new URLSearchParams(list.query())].filter(([key, value]) => !(key === 'status' && value === 'all'))).toString();
  const members = useQuery({ queryKey: adminKeys.members(query), queryFn: () => api<AdminMembersDto>('GET', `/api/admin/members?${query}`), placeholderData: keepPreviousData });
  const domains = useAdminDomains(capabilities.directoryTeams);
  const [confirm, setConfirm] = useState<{ kind: 'sessions' | 'deactivate'; member: AdminMemberDto } | null>(null);
  const [leaving, setLeaving] = useState<AdminMemberDto | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
  const setAccess = useMutation({
    mutationFn: ({ member, active }: { member: AdminMemberDto; active: boolean }) => api('PUT', `/api/tenant/members/${member.userId}/access`, { active }),
    onSuccess: () => {
      setConfirm(null);
      refresh();
    },
  });
  const endSessions = useMutation({
    mutationFn: (member: AdminMemberDto) => api<{ sessions: number }>('POST', `/api/tenant/members/${member.userId}/sessions/revoke`),
    onSuccess: (result) => {
      setConfirm(null);
      setDone(t('admin.access.done.sessions', { count: result.sessions }));
    },
  });
  const setRole = useMutation({
    mutationFn: ({ member, role }: { member: AdminMemberDto; role: TenantRole }) => api('PATCH', `/api/tenant/members/${member.userId}`, { role }),
    onSettled: refresh,
  });
  const data = members.data;
  const roles: RoleKind[] = capabilities.directoryTeams ? ['owner', 'domain_admin', 'admin', 'member'] : ['owner', 'admin', 'member'];
  const statusValue = status === 'all' ? undefined : (status as AdminMemberDto['status'] | undefined);
  const noun = statusValue === 'active' ? t('admin.access.nounActive') : t('admin.access.noun');

  const columns: Column<AdminMemberDto>[] = [
    {
      id: 'member',
      header: t('admin.access.columns.member'),
      sort: 'name',
      cell: (m) => (
        <span className="person-cell">
          <span className="person-cell__name">
            {m.name}
            {m.userId === me.user.id ? <span className="muted"> ({t('common.you')})</span> : null}
          </span>
          <span className="person-cell__email">{m.email}</span>
        </span>
      ),
    },
    ...(capabilities.directoryTeams
      ? [
          {
            id: 'domains',
            header: t('admin.access.columns.domains'),
            width: 'md' as const,
            cell: (m: AdminMemberDto) => {
              const names = m.domains.map((d) => d.name).join(' · ');
              return names ? <span title={names}>{names}</span> : <span className="muted">—</span>;
            },
          },
        ]
      : []),
    { id: 'role', header: t('admin.access.columns.role'), width: 'lg', sort: 'role', cell: (m) => <RoleCell member={m} me={me} {...(organization ? { onRole: (role: TenantRole) => setRole.mutate({ member: m, role }) } : {})} /> },
    { id: 'lastSignIn', header: t('admin.access.columns.lastSignIn'), width: 'md', sort: 'lastSignIn', cell: (m) => <LastSignIn at={m.lastSignInAt} /> },
    { id: 'status', header: t('admin.access.columns.status'), width: 'md', cell: (m) => <StatusBadge status={m.status} /> },
  ];

  const rowMenu = (m: AdminMemberDto) => {
    const self = m.userId === me.user.id;
    const manage = organization && !self && (me.tenant.role === 'owner' || m.role !== 'owner');
    return (
      <Menu trigger={<Icons.more size={18} />} label={t('admin.access.menu.label', { name: m.name })} align="right" portal triggerClassName="dt-row-menu" data-testid="member-menu">
        <Link className="ui-menu-item" to={`/admin/usage/members?q=${encodeURIComponent(m.email)}`} data-testid="member-menu-usage">
          <Icons.usage size={16} />
          <span>{t('admin.access.menu.usage')}</span>
        </Link>
        <Link className="ui-menu-item" to={`/admin/audit?person=${m.userId}&who=${encodeURIComponent(m.name)}&days=30`} data-testid="member-menu-audit">
          <Icons.audit size={16} />
          <span>{t('admin.access.menu.audit')}</span>
        </Link>
        {manage ? (
          <>
            <MenuItem icon={<Icons.logout size={16} />} onClick={() => setConfirm({ kind: 'sessions', member: m })} data-testid="member-menu-sessions">
              {t('admin.access.menu.sessions')}
            </MenuItem>
            <MenuSeparator />
            {m.status === 'deactivated' ? (
              <MenuItem icon={<Icons.lock size={16} />} onClick={() => setAccess.mutate({ member: m, active: true })} data-testid="member-menu-access">
                {t('admin.access.menu.reactivate')}
              </MenuItem>
            ) : (
              <MenuItem icon={<Icons.lock size={16} />} danger onClick={() => setConfirm({ kind: 'deactivate', member: m })} data-testid="member-menu-access">
                {t('admin.access.menu.deactivate')}
              </MenuItem>
            )}
            {!capabilities.directoryLogin ? (
              <MenuItem icon={<Icons.trash size={16} />} danger onClick={() => setLeaving(m)} data-testid="member-menu-remove">
                {t('admin.access.menu.remove')}
              </MenuItem>
            ) : null}
          </>
        ) : null}
      </Menu>
    );
  };

  return (
    <div className="stack">
      {!capabilities.directoryLogin && organization ? <InviteCard me={me} /> : null}
      <div className="dt-toolbar">
        <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.access.search')} testId="members-search" />
        {capabilities.directoryTeams ? (
          <FilterMenu
            label={t('admin.access.domain')}
            value={list.get('domain')}
            allLabel={t('admin.access.allDomains', { count: domains.data?.total ?? 0 })}
            options={(domains.data?.rows ?? []).map((d) => ({ value: d.id, label: d.name }))}
            onChange={(domain) => list.set({ domain })}
            testId="members-domain"
          />
        ) : null}
        <FilterMenu
          label={t('admin.access.role')}
          value={list.get('role')}
          allLabel={t('admin.access.all')}
          options={roles.map((role) => ({ value: role, label: t(`admin.access.roles.${role}`) }))}
          onChange={(role) => list.set({ role })}
          testId="members-role"
        />
        <FilterChips
          label={t('admin.access.columns.status')}
          value={statusValue}
          onChange={(value) => list.set({ status: value ?? 'all' })}
          chips={[
            { value: 'active', label: t('admin.access.status.active'), ...(data ? { count: data.counts.active } : {}) },
            { value: 'deactivated', label: t('admin.access.status.deactivated'), tone: 'danger', ...(data ? { count: data.counts.deactivated } : {}) },
            { value: 'inactive', label: t('admin.access.status.inactive'), tone: 'neutral', ...(data ? { count: data.counts.inactive } : {}) },
          ]}
        />
        <span className="dt-toolbar__end">
          <CsvLink href={`/api/admin/members.csv?${query}`} label={t('admin.csv')} testId="members-csv" />
        </span>
      </div>
      {done ? <Alert tone="success" title={done} data-testid="members-done" /> : null}
      <ErrorAlert error={members.error ?? setRole.error ?? setAccess.error} />
      {!data && members.isPending ? <Skeleton block /> : null}
      {data ? (
        <DataTable
          label={t('admin.access.tabs.members')}
          columns={columns}
          rows={data.rows}
          rowKey={(m) => m.userId}
          sort={sort}
          onSort={onSort}
          rowMenu={rowMenu}
          rowTestId={(m) => `member-${m.email}`}
          loading={members.isFetching}
          data-testid="members-table"
          footer={<Pagination page={list.page} size={list.size} total={data.total} noun={noun} onPage={(page) => list.set({ page: String(page) })} onSize={(size) => list.set({ size: String(size) })} />}
        />
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={t(confirm.kind === 'sessions' ? 'admin.access.confirm.sessionsTitle' : 'admin.access.confirm.deactivateTitle', { name: confirm.member.name })}
          confirmLabel={t(confirm.kind === 'sessions' ? 'admin.access.menu.sessions' : 'admin.access.menu.deactivate')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={setAccess.isPending || endSessions.isPending}
          error={<ErrorAlert error={setAccess.error ?? endSessions.error} />}
          onConfirm={() => (confirm.kind === 'sessions' ? endSessions.mutate(confirm.member) : setAccess.mutate({ member: confirm.member, active: false }))}
          onClose={() => setConfirm(null)}
          data-testid="member-confirm"
        >
          {t(confirm.kind === 'sessions' ? 'admin.access.confirm.sessionsBody' : 'admin.access.confirm.deactivateBody')}
        </ConfirmDialog>
      ) : null}
      {leaving ? <RemoveDialog member={leaving} me={me} onClose={() => setLeaving(null)} /> : null}
    </div>
  );
}

/** The bireysel edition: someone leaves the organization; their work is handed over first (SA-067). */
function RemoveDialog({ member, me, onClose }: { member: AdminMemberDto; me: MeResponse; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const all = useMembers();
  const target: MemberDto = { userId: member.userId, email: member.email, displayName: member.name, role: member.role, joinedAt: member.joinedAt };
  return (
    <Dialog title={t('admin.access.menu.remove')} closeLabel={t('common.close')} onClose={onClose} data-testid="remove-dialog">
      {all.data ? (
        <HandOver
          member={target}
          me={me}
          members={all.data}
          onCancel={onClose}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
            void queryClient.invalidateQueries({ queryKey: keys.members });
            onClose();
          }}
        />
      ) : (
        <Skeleton block />
      )}
    </Dialog>
  );
}

function DomainsTab() {
  const { t, i18n } = useTranslation();
  const list = useListParams();
  const { sort, onSort } = sortState(list, 'name', (key) => key === 'members');
  const query = list.query();
  const domains = useQuery({ queryKey: ['admin', 'domains', 'page', query], queryFn: () => api<PageDto<AdminDomainDto>>('GET', `/api/admin/domains?${query}`), placeholderData: keepPreviousData });
  const columns: Column<AdminDomainDto>[] = [
    {
      id: 'domain',
      header: t('admin.access.domains.columns.domain'),
      sort: 'name',
      cell: (d) => (
        <span className="person-cell">
          <span className="person-cell__name">{d.name}</span>
          <code className="person-cell__code">{d.code}</code>
        </span>
      ),
    },
    {
      id: 'members',
      header: t('admin.access.domains.columns.members'),
      width: 'xs',
      numeric: true,
      sort: 'members',
      cell: (d) => (
        <Link to={`/admin/access/members?domain=${d.id}&status=all`} data-testid="domain-members-link">
          {formatCount(d.members, i18n.language)}
        </Link>
      ),
    },
    { id: 'admins', header: t('admin.access.domains.columns.admins'), width: 'lg', cell: (d) => (d.admins.length > 0 ? d.admins.map((a) => a.name).join(', ') : <span className="muted">{t('admin.access.domains.none')}</span>) },
    { id: 'workspace', header: t('admin.access.domains.columns.workspace'), width: 'md', cell: (d) => d.workspace.name },
    {
      id: 'skills',
      header: t('admin.access.domains.columns.skills'),
      cell: (d) =>
        d.skills.length > 0 ? (
          <span className="mono-list">
            {d.skills
              .slice(0, 3)
              .map((s) => s.name)
              .join(', ')}
            {d.skills.length > 3 ? <span className="muted"> {t('admin.access.domains.more', { count: d.skills.length - 3 })}</span> : null}
          </span>
        ) : (
          <span className="muted">{t('admin.access.domains.none')}</span>
        ),
    },
  ];
  return (
    <div className="stack">
      <div className="dt-toolbar">
        <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.access.domains.search')} testId="domains-search" />
      </div>
      <ErrorAlert error={domains.error} />
      {domains.data ? (
        <DataTable
          label={t('admin.access.tabs.domains')}
          columns={columns}
          rows={domains.data.rows}
          rowKey={(d) => d.id}
          sort={sort}
          onSort={onSort}
          rowTestId={(d) => `domain-${d.code}`}
          data-testid="domains-table"
          footer={<Pagination page={list.page} size={list.size} total={domains.data.total} noun={t('admin.access.domains.noun')} onPage={(page) => list.set({ page: String(page) })} onSize={(size) => list.set({ size: String(size) })} />}
        />
      ) : (
        <Skeleton block />
      )}
    </div>
  );
}

function SystemTab({ accounts, loading, error }: { accounts: SystemAccountDto[] | undefined; loading: boolean; error: unknown }) {
  const { t, i18n } = useTranslation();
  const columns: Column<SystemAccountDto>[] = [
    {
      id: 'account',
      header: t('admin.access.system.columns.account'),
      cell: (a) => (
        <span className="person-cell">
          <span className="person-cell__name">{a.name}</span>
          <span className="person-cell__email">{a.email}</span>
        </span>
      ),
    },
    { id: 'status', header: t('admin.access.columns.status'), width: 'md', cell: (a) => <StatusBadge status={a.active ? 'active' : 'deactivated'} /> },
    { id: 'twoFactor', header: t('admin.access.system.columns.twoFactor'), width: 'md', cell: (a) => (a.twoFactor ? <Badge tone="success">{t('admin.access.system.on')}</Badge> : <Badge tone="warning">{t('admin.access.system.off')}</Badge>) },
    { id: 'lastUsed', header: t('admin.access.system.columns.lastUsed'), width: 'md', cell: (a) => (a.lastUsedAt ? <LastSignIn at={a.lastUsedAt} /> : <span className="muted">{t('admin.access.system.never')}</span>) },
    { id: 'created', header: t('admin.access.system.columns.created'), cell: (a) => new Date(a.createdAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) },
  ];
  return (
    <div className="stack">
      <p className="muted" data-testid="system-hint">
        {t('admin.access.system.hint')}
      </p>
      <ErrorAlert error={error} />
      {loading ? <Skeleton block /> : null}
      {accounts ? <DataTable label={t('admin.access.tabs.system')} columns={columns} rows={accounts} rowKey={(a) => a.userId} rowTestId={(a) => `system-${a.email}`} empty={t('admin.access.system.empty')} data-testid="system-table" /> : null}
    </div>
  );
}

/** D-113: sign-ins of a rebuilt directory that wait for "Hesabı bağla". */
function RelinkCard() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const requests = useQuery({ queryKey: adminKeys.relinks, queryFn: () => api<RelinkRequestDto[]>('GET', '/api/admin/identity/relinks') });
  const link = useMutation({
    mutationFn: (id: string) => api('POST', `/api/admin/identity/relinks/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.relinks });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
    },
  });
  if (!requests.data || requests.data.length === 0) return null;
  return (
    <section className="relink-card" aria-labelledby="relink-title" data-testid="relink-card">
      <h2 id="relink-title">
        <Icons.warning size={16} />
        {t('admin.access.relink.title')}
      </h2>
      <p className="muted">{t('admin.access.relink.hint')}</p>
      <ErrorAlert error={link.error} />
      <ul className="relink-card__list">
        {requests.data.map((r) => (
          <li key={r.id} className="relink-card__row" data-testid={`relink-${r.user.email}`}>
            <span>
              <strong>{r.user.name}</strong> <span className="muted">{r.user.email}</span>
              <br />
              <span className="muted">
                {t('admin.access.relink.detail', {
                  username: r.username ?? r.email,
                  count: r.attempts,
                  when: new Date(r.lastAttemptAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
                })}
              </span>
            </span>
            <Button size="sm" busy={link.isPending && link.variables === r.id} onClick={() => link.mutate(r.id)} data-testid="relink-link">
              {t('admin.access.relink.link')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
